import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "@emotion/styled";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  TextField,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { ReactComponent as Logo } from "../../vendor/logo.svg";
import colors from "../../utils/colors";
import axios from "../../utils/axios/v1/userAxios";
import {
  validateFirstName,
  validateLastName,
  validateOrganisationDisplayName,
  getCharPolicyFieldText,
} from "../../utils/validation";

const Brand = styled(Logo)`
  fill: ${(props) => props.theme.palette.primary.main};
  width: 280px;
  height: 68px;
  margin-bottom: 28px;
`;

const PageWrapper = styled(Box)`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  background: linear-gradient(135deg, ${colors.primary[100]} 0%, ${colors.neutral[50]} 60%, ${colors.neutral[100]} 100%);
`;

const Card = styled(Paper)`
  width: 100%;
  max-width: 520px;
  padding: 36px 32px 32px;
  border-radius: 20px;
  box-shadow: 0px 20px 60px rgba(17, 17, 17, 0.08);
  border: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[100]};
`;

const IconBubble = styled(Box)`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px auto;
  background: ${colors.primary[100]};
  border: 1px solid ${colors.primary.alpha || colors.primary[100]};
  box-shadow: 0 8px 24px rgba(99, 113, 247, 0.15);
`;

function errMessage(err) {
  if (typeof err === "string") return err;
  if (err?.message) return err.message;
  return "Something went wrong";
}

function CompleteOnboarding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const isMountedRef = useRef(true);

  const [loadingContext, setLoadingContext] = useState(true);
  const [contextError, setContextError] = useState("");
  const [context, setContext] = useState(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const email = context?.user?.email || "";

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadContext = useCallback(async () => {
    if (!token) {
      setContextError("Missing invitation link. Please use the link from your email.");
      setLoadingContext(false);
      return;
    }
    setLoadingContext(true);
    setContextError("");
    try {
      const { data } = await axios.get("/auth/onboarding/context", { params: { token } });
      if (!isMountedRef.current) return;
      setContext(data);
      setFirstName(data?.user?.firstName || "");
      setLastName(data?.user?.lastName || "");
      setDisplayName(data?.organisation?.displayName || data?.organisation?.title || "");
    } catch (err) {
      if (!isMountedRef.current) return;
      setContextError(errMessage(err));
    } finally {
      if (isMountedRef.current) setLoadingContext(false);
    }
  }, [token]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const errors = {};
    const dnCheck = validateOrganisationDisplayName(displayName);
    if (!dnCheck.valid) errors.displayName = dnCheck.message;
    const fnCheck = validateFirstName(firstName);
    if (!fnCheck.valid) errors.firstName = fnCheck.message;
    const lnCheck = validateLastName(lastName);
    if (!lnCheck.valid) errors.lastName = lnCheck.message;
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    if (password.length < 6) {
      setSubmitError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post("/auth/onboarding/complete", {
        token,
        password,
        confirmPassword,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName: displayName.trim(),
      });
      if (!isMountedRef.current) return;
      setSuccess(true);
      setTimeout(() => {
        navigate("/auth/login", { replace: true });
      }, 2800);
    } catch (err) {
      if (isMountedRef.current) {
        setSubmitError(errMessage(err));
      }
    } finally {
      if (isMountedRef.current) setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <Helmet title="Complete setup" />
      <Stack alignItems="center" sx={{ width: "100%", maxWidth: 560, py: { xs: 1, sm: 2 } }}>
        <Brand />
        <Card elevation={0}>
          {loadingContext && (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Loading your invitation…</Typography>
            </Stack>
          )}

          {!loadingContext && contextError && (
            <Stack spacing={2}>
              <Alert severity="error">{contextError}</Alert>
              <Button variant="outlined" onClick={() => navigate("/auth/login", { replace: true })}>
                Back to login
              </Button>
            </Stack>
          )}

          {!loadingContext && !contextError && context && !success && (
            <>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 0.75,
                  textAlign: "center",
                }}
              >
                Complete your organisation setup
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, textAlign: "center", lineHeight: 1.5 }}
              >
                Finish setup for <strong>{email}</strong>
              </Typography>

              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submitError}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label="Organisation display name"
                    value={displayName}
                    onChange={(e) => {
                      setDisplayName(e.target.value);
                      if (fieldErrors.displayName) {
                        setFieldErrors((prev) => ({ ...prev, displayName: undefined }));
                      }
                    }}
                    fullWidth
                    required
                    autoFocus
                    placeholder="Organisation name"
                    inputProps={{ maxLength: 25 }}
                    error={Boolean(fieldErrors.displayName)}
                    helperText={getCharPolicyFieldText("organisationName", fieldErrors.displayName)}
                  />
                  <TextField
                    label="First name"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (fieldErrors.firstName) {
                        setFieldErrors((prev) => ({ ...prev, firstName: undefined }));
                      }
                    }}
                    fullWidth
                    required
                    placeholder="First name"
                    error={Boolean(fieldErrors.firstName)}
                    helperText={getCharPolicyFieldText("personName", fieldErrors.firstName) || undefined}
                  />
                  <TextField
                    label="Last name"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      if (fieldErrors.lastName) {
                        setFieldErrors((prev) => ({ ...prev, lastName: undefined }));
                      }
                    }}
                    fullWidth
                    required
                    placeholder="Last name"
                    error={Boolean(fieldErrors.lastName)}
                    helperText={getCharPolicyFieldText("personName", fieldErrors.lastName) || undefined}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    required
                    autoComplete="new-password"
                    placeholder="Create a password"
                  />
                  <TextField
                    label="Confirm password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                    required
                    autoComplete="new-password"
                    placeholder="Confirm your password"
                  />
                  <Button type="submit" variant="contained" size="large" disabled={submitting} sx={{ mt: 1 }}>
                    {submitting ? <CircularProgress size={24} color="inherit" /> : "Save and continue"}
                  </Button>
                </Stack>
              </form>
            </>
          )}

          {success && (
            <Stack alignItems="center" spacing={2} py={2}>
              <IconBubble>
                <CheckCircleIcon sx={{ fontSize: 44, color: colors.primary[300] }} />
              </IconBubble>
              <Typography variant="h6" fontWeight={700} textAlign="center">
                You&apos;re all set
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Redirecting to login…
              </Typography>
            </Stack>
          )}
        </Card>
      </Stack>
    </PageWrapper>
  );
}

export default CompleteOnboarding;
